import {
    GetSpeculativeProjectsDocument,
    In_Queue_Speculative_Projects_Insert_Input,
    useCreateSpeculativeProjectsMutation
} from "generated/graphql";
import { useCluster } from "in_queue/contexts/ClusterContext";
import { useSubscriptions } from "in_queue/contexts/InQueueSubscriptionsContext";
import {
    useSpeculativeProjectsInCluster,
    useSubscriptionForCluster
} from "in_queue/contexts/ProjectDataContext";
import { ModelPOI } from "in_queue/types/modelPOI";
import { SpeculativeProjectComponent } from "in_queue/types/projectType";
import { FuelComponentTypes } from "in_queue/types/speculativeProjectType";

interface CreateSpeculativeProjectResponse {
    id: string;
    project_id: string;
}

interface CreateSpeculativeProjectProps {
    projectComponents: Record<FuelComponentTypes, SpeculativeProjectComponent>;
    projectName: string;
    selectedPOI: ModelPOI;
}

export const useCreateSpeculativeProject: () => (
    props: CreateSpeculativeProjectProps
) => Promise<CreateSpeculativeProjectResponse | undefined> = () => {
    const [createSpeculativeProjectsMutation] =
        useCreateSpeculativeProjectsMutation();

    const cluster = useCluster();
    const subscription = useSubscriptionForCluster();
    const subscriptions = useSubscriptions();
    const existingSpeculativeProjects = useSpeculativeProjectsInCluster();

    const createSpeculativeProject = async ({
        projectComponents,
        projectName,
        selectedPOI
    }: CreateSpeculativeProjectProps) => {
        if (
            subscription === "loading" ||
            subscription === undefined ||
            subscriptions === "loading" ||
            existingSpeculativeProjects === "loading"
        ) {
            throw new Error("Unable to create speculative project");
        }

        const parentComponent = projectComponents[FuelComponentTypes.MFO];
        if (!parentComponent) {
            throw new Error("Unable to create speculative project");
        }

        const subscriptionId = subscription.subscriptionId;
        const subscriptionIds = subscriptions.map((sub) => sub.subscriptionId);

        const existingSpeculativeProjectIds = existingSpeculativeProjects.map(
            (project) => project.projectId
        );
        const parentProjectId = generateProjectId(
            existingSpeculativeProjectIds
        );
        const parentProject = buildSpeculativeProjectQuery({
            component: parentComponent,
            projectName,
            projectId: parentProjectId,
            clusterId: cluster.id,
            poiId: selectedPOI.id,
            subscriptionId: subscriptionId
        });
        const childrenProjects: In_Queue_Speculative_Projects_Insert_Input[] =
            Object.entries(projectComponents)
                .filter(([key]) => key !== FuelComponentTypes.MFO)
                .map(([key, component]) =>
                    buildSpeculativeProjectQuery({
                        component,
                        projectName: `${projectName} - ${key} component`,
                        projectId: parentProjectId,
                        clusterId: cluster.id,
                        poiId: selectedPOI.id,
                        subscriptionId
                    })
                );
        const result = await createSpeculativeProjectsMutation({
            variables: {
                objects: {
                    ...parentProject,
                    in_queue_speculative_projects_children: {
                        data: childrenProjects
                    }
                }
            },
            refetchQueries: [
                {
                    query: GetSpeculativeProjectsDocument,
                    variables: {
                        subscriptionIds
                    }
                }
            ]
        });

        if (
            result.data?.insert_in_queue_speculative_projects?.affected_rows !==
            Object.keys(projectComponents).length
        ) {
            throw new Error("Unable to create speculative project");
        }

        return result.data?.insert_in_queue_speculative_projects?.returning[0];
    };

    return createSpeculativeProject;
};

const generateProjectId = (existingProjectIds: string[]): string => {
    const existingIdsSet = new Set(existingProjectIds);

    let generatedProjectId = undefined;
    while (generatedProjectId === undefined) {
        const letters = String.fromCharCode(
            65 + Math.floor(Math.random() * 26),
            65 + Math.floor(Math.random() * 26)
        );
        const numbers = Math.floor(Math.random() * 10000)
            .toString()
            .padStart(4, "0");
        const projectId = `${letters}-${numbers}`;

        // We should make sure that the generated project ID is not already used by an
        // existing speculative project for the cluster
        if (!existingIdsSet.has(projectId)) {
            generatedProjectId = projectId;
        }
    }

    return generatedProjectId;
};

const buildSpeculativeProjectQuery = (props: {
    component: SpeculativeProjectComponent;
    projectName: string;
    projectId: string;
    clusterId: string;
    poiId: string;
    subscriptionId: string;
}): In_Queue_Speculative_Projects_Insert_Input => ({
    cluster_id: props.clusterId,
    poi_id: props.poiId,
    subscription_id: props.subscriptionId,
    project_id: props.projectId,
    project_name: props.projectName,
    fuel_type: props.component.fuelType,
    mw_capacity: props.component.size.nrisMw,
    mw_energy: props.component.size.erisMw,
    mw_mfo: props.component.size.erisMw
});
